import React from 'react';
import { Box, Button, Chip, IconButton, Stack, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { FinanceStageSetting } from '../../types/pettyCash';

/**
 * The finance stages after the PR flow: the finance review (fixed, the review role of the settings), then the extra
 * stages in order, each approved by any holder of its role. Only the review changes the receipts; the last stage
 * approves the posting to SAP.
 */

export const MAX_FINANCE_STAGES = 5;

interface Props {
  reviewRole: string;
  stages: FinanceStageSetting[];
  onChange: (stages: FinanceStageSetting[]) => void;
}

const StageNumber: React.FC<{ n: number }> = ({ n }) => (
  <Box
    aria-hidden="true"
    sx={{
      width: 24, height: 24, borderRadius: '50%', flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center',
      border: '1px solid #9aa6ad', fontSize: 12, fontWeight: 600,
    }}
  >
    {n}
  </Box>
);

const FinanceStagesEditor: React.FC<Props> = ({ reviewRole, stages, onChange }) => {
  const update = (i: number, patch: Partial<FinanceStageSetting>) => onChange(stages.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const move = (i: number, by: -1 | 1) => {
    const next = [...stages];
    const [moved] = next.splice(i, 1);
    next.splice(i + by, 0, moved);
    onChange(next);
  };
  return (
    <Box>
      <Typography variant="subtitle2">Finance stages</Typography>
      <Typography variant="body2" color="text.secondary">
        After the PR flow&apos;s stages a claim goes through the finance review, then each stage below in order. Only the finance review changes the
        receipts; the last stage approves the posting to SAP.
      </Typography>
      <Stack component="ol" spacing={1.5} sx={{ listStyle: 'none', p: 0, m: 0, mt: 1.5 }}>
        <Stack component="li" direction="row" spacing={1} alignItems="center">
          <StageNumber n={1} />
          <Typography variant="body2">Finance review ({reviewRole || 'the finance review role'})</Typography>
          <Chip size="small" variant="outlined" label="Always first" />
        </Stack>
        {stages.map((s, i) => (
          <Stack component="li" key={i} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
            <Box sx={{ pt: { sm: 1 } }}><StageNumber n={i + 2} /></Box>
            <TextField
              id={`pc-fin-stage-${i}-name`}
              size="small"
              label="Stage name"
              value={s.name}
              onChange={(e) => update(i, { name: e.target.value.slice(0, 100) })}
              placeholder="e.g. Finance manager approval"
              required
              sx={{ flex: '1 1 0' }}
            />
            <TextField
              id={`pc-fin-stage-${i}-role`}
              size="small"
              label="Role"
              value={s.role}
              onChange={(e) => update(i, { role: e.target.value.trim().slice(0, 100) })}
              helperText="Any user holding this role (main or additional) in the company"
              required
              sx={{ flex: '1 1 0' }}
            />
            <Stack direction="row" spacing={0.5} sx={{ pt: { sm: 0.5 } }}>
              <IconButton size="small" aria-label={`Move stage ${i + 2} up`} disabled={i === 0} onClick={() => move(i, -1)}><ArrowUpwardIcon fontSize="small" /></IconButton>
              <IconButton size="small" aria-label={`Move stage ${i + 2} down`} disabled={i === stages.length - 1} onClick={() => move(i, 1)}><ArrowDownwardIcon fontSize="small" /></IconButton>
              <IconButton size="small" aria-label={`Remove stage ${i + 2}`} onClick={() => onChange(stages.filter((_s, j) => j !== i))}><DeleteOutlineIcon fontSize="small" /></IconButton>
            </Stack>
          </Stack>
        ))}
      </Stack>
      <Button
        size="small"
        variant="outlined"
        startIcon={<AddIcon />}
        sx={{ mt: 1.5 }}
        disabled={stages.length >= MAX_FINANCE_STAGES}
        onClick={() => onChange([...stages, { name: '', role: '' }])}
      >
        Add a stage
      </Button>
      {stages.length >= MAX_FINANCE_STAGES ? (
        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>At most {MAX_FINANCE_STAGES} stages after the review</Typography>
      ) : null}
    </Box>
  );
};

export default FinanceStagesEditor;
